import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {
    BillingContractModel,
    BillingCounterpartyModel,
    GovernmentModel,
    OrganizationManagerModel,
    OrganizationTariffModel,
    PageOrganizationManagerModel
} from '@services/back-office';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {OpenApiService} from '@shared/services/openApi.service';
import {BillingService} from '@shared/services/billing.service';
import {TranslateService} from '@ngx-translate/core';
import {ToastrService} from 'ngx-toastr';
import {SharedService} from '@shared/shared.service';
import {ConfigService} from '@config/config.service';
import {select, Store} from '@ngrx/store';
import {Reducers} from '@app/reducers';
import {Actions, ofType} from '@ngrx/effects';
import {BehaviorSubject, catchError, EMPTY, Observable, Subject, takeUntil} from 'rxjs';
import {PermissionsModel} from '@shared/models/permission.model';
import * as selectFromLogin from '@login/ngrx/login.selectors';
import {filter, map, take} from 'rxjs/operators';
import {OAuth2TokensDto} from '@services/authentication';
import * as counterpartyActions from '@billing/ngrx/actions/counterparty.actions';
import * as counterpartiesActions from '@billing/ngrx/actions/counterparty.actions';
import {environment} from '@environments/environment';
import {ConfirmationDialogComponent} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {ListItemType} from '@shared/enum/list-item-type';
import * as contactActions from '@billing/ngrx/actions/contracts.actions';
import {MatTableDataSource} from '@angular/material/table';
import {AssociationsService} from '@shared/services/associations.service';
import {CounterpartyRoleDialogComponent2} from '@billing/components/counterparty/counterparty-role-dialog2/counterparty-role-dialog.component';
import {CounterpartyTariffDialogComponent} from '@billing/components/counterparty/counterparty-tariff-dialog/counterparty-tariff-dialog.component';
import {CounterpartyTariffHistoryComponent} from '@billing/components/counterparty/counterparty-tariff-history/counterparty-tariff-history.component';
import {CounterpartyManagerDialogComponent} from '@billing/components/counterparty/counterparty-manager-dialog/counterparty-manager-dialog.component';

@Component({
    selector: 'app-counterparty-view-dialog',
    templateUrl: './counterparty-view-dialog.component.html',
    styleUrl: './counterparty-view-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CounterpartyViewDialogComponent implements OnInit, OnDestroy {
    /** Loading */
    loading$ = new BehaviorSubject(false);
    /** Permissions */
    permissions$: Observable<PermissionsModel[] | null>;
    permissions: PermissionsModel[] | null;
    /** Tables */
    firstTabLoaded = false;

    roleDataSource: MatTableDataSource<GovernmentModel> = new MatTableDataSource<GovernmentModel>();
    roleColumns: string[] = ['user', 'roleType', 'role', 'actions'];
    secondTabLoaded = false;

    contractsDataSource: MatTableDataSource<BillingContractModel> = new MatTableDataSource<BillingContractModel>();
    columns: string[] = ['name', 'amount', 'actions'];
    contractsTabLoaded = false;

    managerDataSource: MatTableDataSource<OrganizationManagerModel> = new MatTableDataSource<OrganizationManagerModel>();
    managerColumns: string[] = ['manager', 'role', 'actions'];
    managersTabLoaded = false;

    canAddMainAdmin = false;
    /** Form */
    counterpartyForm: UntypedFormGroup;
    saved = false;

    canAddContractSaas = true;
    canAddContractAcc = true;
    canAddContractPersonal = true;
    public environment = environment;

    private destroyed$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            counterparty: BillingCounterpartyModel,
            disableAddContract?: boolean,
        },
        public dialogRef: MatDialogRef<CounterpartyViewDialogComponent>,
        private openApi: OpenApiService,
        private associationsService: AssociationsService,
        private billingService: BillingService,
        private changeDetectorRef: ChangeDetectorRef,
        private translateService: TranslateService,
        private toastrService: ToastrService,
        private sharedService: SharedService,
        private config: ConfigService,
        private store$: Store<Reducers>,
        private formBuilder: UntypedFormBuilder,
        private actions$: Actions,
    ) {
        /** Permissions */
        this.permissions$ = this.store$.pipe(select(selectFromLogin.selectPermissions));
    }

    ngOnInit(): void {
        /** Get Permissions from Observable and Filter Navigation Items */
        this.getPermissions();
        this.tabChanged({index: 0});

        if (!this.counterpartyForm) {
            this.initForm();
        }

        this.watchEditCounterpartySuccess();
    }

    ngOnDestroy() {
        this.destroyed$.complete();
        this.loading$.complete();
    }

    checkPermissions(permission: string): boolean {
        return this.permissions ?
            SharedService.checkPermission(permission, this.permissions[0].permissions, this.permissions[0].permissionsExists) :
            false;
    }

    initForm() {
        this.counterpartyForm = this.formBuilder.group({});
    }

    patchForm() {
        if (!this.counterpartyForm) {
            this.initForm();
        }
        this.counterpartyForm.patchValue({});
    }

    updateCounterparty() {
        this.billingService.getCounterparty(this.data.counterparty.id!).pipe(
            takeUntil(this.destroyed$),
            map((res) => {
                this.data.counterparty = res;
                this.store$.dispatch(new counterpartiesActions.EditCounterpartiesSuccess(res));
            })
        ).subscribe();
    }

    editCounterparty() {
        if (!this.data.counterparty.id) {
            return;
        }

        this.sharedService.openCrmItem(ListItemType.COUNTERPARTY, this.data.counterparty.id);
    }

    deleteCounterparty() {
        const dialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    'modules.billing.counterParty.actions.confirmDeleteAgreement',
                    {name: this.data.counterparty?.name ?? ''}
                ),
                cancelText: 'common.buttons.cancel',
                submitText: 'common.buttons.delete',
            }
        );

        dialog$.afterClosed().pipe().subscribe((result) => {
            if (result && this.data.counterparty.id) {
                this.store$.dispatch(new counterpartiesActions.DeleteCounterparties(this.data.counterparty.id));
            }
        });
    }

    /** Get Permissions from Observable and Filter Navigation Items */
    getPermissions() {
        this.permissions$.pipe(
            filter(data => !!data),
            take(1)
        ).subscribe((permissions: PermissionsModel[] | null) => {
                this.permissions = permissions;
            }
        );
    }

    tabChanged(event: any) {
        switch (event.index) {
            case 0:
                if (!this.saved) {
                    this.patchForm();
                }
                break;
            case 1:
                if (!this.secondTabLoaded) {
                    this.getRoles();
                }
                break;
            case 2:
                if (!this.contractsTabLoaded) {
                    this.getContracts();
                }
                break;
            case 3:
                if (!this.managersTabLoaded) {
                    this.getManagers();
                }
                break;
            default:
                return;
        }
    }

    /** Roles == Governments */
    getRoles() {
        this.loading$.next(true);
        this.associationsService.loadRoles(this.data.counterparty.id!).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((result: GovernmentModel[]) => {
                this.roleDataSource.data = result;
                this.changeDetectorRef.detectChanges();
                this.loading$.next(false);
                this.secondTabLoaded = true;
                this.getCanAddAdmin(result);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    deleteRole(element: GovernmentModel) {
        const dialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    'modules.clients.associations.associationViewDialog.deleteRoleWarning',
                    {name: element.user?.userName?.fullName}
                ),
                cancelText: 'common.buttons.cancel',
                submitText: 'common.buttons.delete',
            }
        );

        dialog$.afterClosed().pipe(
            take(1)
        ).subscribe((result) => {
            if (result) {
                this.associationsService.deleteGovernment(element.id!).pipe(
                    take(1),
                    map(() => {
                        const positions = this.roleDataSource.data;
                        if (positions && positions.length) {
                            const positionIndex = positions.findIndex((item: GovernmentModel) => item.user?.userId === element.user?.userId);

                            if (positionIndex !== -1) {
                                positions.splice(positionIndex, 1);
                                this.roleDataSource.data = positions;
                                this.changeDetectorRef.detectChanges();
                            }
                        }

                        if (this.data.counterparty.tariff == 'SOCIAL') {
                            this.updateCounterparty();
                        }

                        this.getCanAddAdmin(positions);
                    })
                ).subscribe();
            }
        });
    }

    getCanAddAdmin(governments: GovernmentModel[]) {
        this.canAddMainAdmin = !governments.find(el => el.admin);
    }

    /** Contracts */
    getContracts() {
        this.billingService.getCounterpartyContracts(this.data.counterparty.id!).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((result: BillingContractModel[]) => {
                for (let i = 0; i < result?.length; i++) {
                    if (result[i].type == "SAAS" && result[i].status != "CANCELLED") {
                        this.canAddContractSaas = false;
                    }

                    if (result[i].type == "ACCOUNTING" && result[i].status != "CANCELLED") {
                        this.canAddContractAcc = false;
                    }

                    if (result[i].type == "PERSONAL_ACCOUNTING" && result[i].status != "CANCELLED") {
                        this.canAddContractPersonal = false;
                    }
                }
                this.contractsDataSource.data = result;
                this.changeDetectorRef.detectChanges();
                this.loading$.next(false);
                this.contractsTabLoaded = true;
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    addContract(type: string) {
        this.sharedService.createCrmItem(ListItemType.CONTRACT, {counterparty: this.data.counterparty, type: type}, true);
    }

    editContract(item: BillingContractModel) {
        this.sharedService.openCrmItem(ListItemType.CONTRACT, item.id!);
    }

    deleteContract(item: BillingContractModel) {
        const dialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    'modules.billing.agreements.actions.confirmDeleteAgreement',
                    {number: item.number ? item.number : ''}
                ),
                cancelText: 'common.buttons.cancel',
                submitText: 'common.buttons.delete',
            }
        );

        dialog$.afterClosed().pipe().subscribe((result) => {
            if (result) {
                this.store$.dispatch(new contactActions.DeleteContracts(item.id!));
            }
        });
    }

    /** Managers DAH */
    getManagers() {
        this.loading$.next(true);
        this.associationsService.getOrganizationManagers({organization: {id: this.data.counterparty.id!}}).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((result: PageOrganizationManagerModel) => {
                this.managerDataSource.data = result.content!;
                this.changeDetectorRef.detectChanges();
                this.loading$.next(false);
                this.managersTabLoaded = true;
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    deleteManager(element: OrganizationManagerModel) {
        const dialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    'modules.billing.counterParty.viewDialog.deleteManagerWarning',
                    {name: element.manager!.name}
                ),
                cancelText: 'common.buttons.cancel',
                submitText: 'common.buttons.yes',
            }
        );

        dialog$.afterClosed().pipe(
            take(1)
        ).subscribe((result) => {
            if (result) {
                this.loading$.next(true);

                this.associationsService.deleteOrganizationManager(element.id!).pipe(
                    map(() => {
                        this.loading$.next(false);
                        const managers = this.managerDataSource.data;
                        if (managers && managers.length) {
                            const managerIndex = managers.findIndex((item: OrganizationManagerModel) => item.id === element.id);

                            if (managerIndex !== -1) {
                                managers.splice(managerIndex, 1);
                                this.managerDataSource.data = managers;
                            }
                        }
                    }),
                    catchError((error: HttpErrorResponse) => {
                        SharedService.showErrorWithTitle(
                            this.translateService,
                            this.toastrService,
                            `responseErrors.${error.error.message}`
                        );
                        this.loading$.next(false);
                        return EMPTY;
                    })
                ).subscribe();
            }
        });
    }

    addItem(currentTab: number | null, args?: any) {
        switch (currentTab) {
            case 0:

                break;
            case 1:
                // eslint-disable-next-line no-case-declarations
                const associationRoleDialog$ = this.sharedService.openDialog(
                    CounterpartyRoleDialogComponent2,
                    {...this.config.addItemDialog, maxWidth: '400px', hasBackdrop: true},
                    {
                        counterparty: this.data.counterparty,
                        ...args && {...args}
                    }
                );

                associationRoleDialog$.afterClosed().pipe(
                    take(1),
                ).subscribe((dialogData: { result: boolean, data?: any }) => {
                    if (dialogData?.result) {
                        this.getRoles();
                    }
                    if (this.data.counterparty.tariff == 'BASE') {
                        this.updateCounterparty();
                    }
                });
                break;
            case 2:
                //договори
                break;
            case 3:
                // eslint-disable-next-line no-case-declarations
                const notAllowedTypes = [];
                if (this.managerDataSource?.data?.length) {
                    for (let i = 0; i < this.managerDataSource.data.length; i++) {
                        notAllowedTypes.push(this.managerDataSource.data[i].position);
                    }

                    if (notAllowedTypes.length == 4) {
                        this.toastrService.info('Працівників зі всіми ролями вже додано. Для зміни видаліть когось і додайте з новою роллю!');
                        break;
                    }
                }
                // eslint-disable-next-line no-case-declarations
                const managerDialog$ = this.sharedService.openDialog(
                    CounterpartyManagerDialogComponent,
                    {...this.config.addItemDialog, hasBackdrop: true},
                    {counterparty: this.data.counterparty, notAllowedTypes}
                );

                managerDialog$.afterClosed().pipe(
                    take(1),
                ).subscribe((dialogData: { result: boolean, data?: OrganizationManagerModel }) => {
                    if (dialogData.result && dialogData.data) {
                        const managers = this.managerDataSource.data;
                        if (managers && managers.length) {
                            managers.unshift(dialogData.data);
                            this.managerDataSource.data = managers;
                        } else {
                            this.managerDataSource.data = [dialogData.data];
                        }

                        this.changeDetectorRef.detectChanges();
                    }
                });
                break;
            default:
                return;
        }
    }

    changeTariff() {
        const tariffDialog$ = this.sharedService.openDialog(
            CounterpartyTariffDialogComponent,
            {...this.config.addItemDialog, hasBackdrop: true},
            {counterparty: this.data.counterparty}
        );

        tariffDialog$.afterClosed().pipe(
            take(1),
        ).subscribe((dialogData: { result: boolean, data?: OrganizationTariffModel }) => {
            if (dialogData.result && dialogData.data) {
                this.data.counterparty = {...this.data.counterparty, tariff: dialogData.data.tariff};
                this.store$.dispatch(new counterpartiesActions.EditCounterpartiesSuccess(this.data.counterparty));
                this.changeDetectorRef.detectChanges();
            }
        });
    }

    toggleExperimentalFeatures(enabled: boolean) {
        this.loading$.next(true);
        this.billingService.toggleCounterpartyExperimentalFeatures(this.data.counterparty.id!, enabled).pipe(
            takeUntil(this.destroyed$),
            map(() => {
                this.data.counterparty = {...this.data.counterparty, experimentalFeatures: enabled};
                this.store$.dispatch(new counterpartiesActions.EditCounterpartiesSuccess(this.data.counterparty));
                this.loading$.next(false);
                this.changeDetectorRef.detectChanges();
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    showTariffHistory() {
        this.sharedService.openDialog(
            CounterpartyTariffHistoryComponent,
            {...this.config.addItemDialog},
            {counterparty: this.data.counterparty}
        );
    }

    openCabinet() {
        this.associationsService.getTempToken(this.data.counterparty.id!).pipe(
            takeUntil(this.destroyed$),
            map((response: OAuth2TokensDto) => {
                if (response) {
                    window.open(
                        // tslint:disable-next-line:max-line-length
                        `${environment.tempCabinetBaseUrl}/external?accessToken=${response.accessToken}&associationId=${this.data.counterparty.id}&cabinetTypeRoute=business-organization`, '_blank', '_blank'
                    );
                }
            })
        ).subscribe();
    }

    watchEditCounterpartySuccess() {
        this.actions$.pipe(
            ofType(counterpartyActions.CounterpartiesActionTypes.EditCounterpartiesSuccess),
            takeUntil(this.destroyed$)
        ).subscribe(() => {
            this.saved = true;
            this.loading$.next(false);
        });
    }

    /** Close dialog */
    closeDialog(result: boolean, data?: any) {
        this.dialogRef.close({result, data});
    }

}
